import React, { useCallback, useEffect, useState } from 'react';
import {
  Users,
  Plus,
  Pencil,
  KeyRound,
  RefreshCw,
  ShieldCheck,
  X,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { useCpPortal } from '../../hooks/useCpPortal';

interface TeamUser {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'maker' | 'checker' | 'viewer';
  isActive: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

const ROLE_OPTIONS: { value: TeamUser['role']; label: string; hint: string }[] = [
  { value: 'admin', label: 'Admin', hint: 'Full access + manage team' },
  { value: 'maker', label: 'Maker', hint: 'Create & submit payment runs' },
  { value: 'checker', label: 'Checker', hint: 'Approve payment runs (2nd authoriser)' },
  { value: 'viewer', label: 'Viewer', hint: 'Read-only access' },
];

const ROLE_STYLES: Record<TeamUser['role'], string> = {
  admin: 'bg-violet-50 text-violet-700 border-violet-200',
  maker: 'bg-sky-50 text-sky-700 border-sky-200',
  checker: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  viewer: 'bg-slate-50 text-slate-600 border-slate-200',
};

const PASSWORD_HINT = 'Min 8 characters, one uppercase letter and one number.';

function passwordValid(pw: string): boolean {
  return pw.length >= 8 && /[A-Z]/.test(pw) && /[0-9]/.test(pw);
}

export const ClientTeamOverlay: React.FC = () => {
  const { apiBase, authHeaders: cpAuthHeaders, user } = useCpPortal();
  const isAdmin = user?.role === 'admin';

  const [users, setUsers] = useState<TeamUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState<TeamUser | null>(null);
  const [resetUser, setResetUser] = useState<TeamUser | null>(null);

  const authHeaders = useCallback((): Record<string, string> => ({
    ...cpAuthHeaders(),
    'Content-Type': 'application/json',
  }), [cpAuthHeaders]);

  const flash = (type: 'success' | 'error', text: string) => {
    setBanner({ type, text });
    window.setTimeout(() => setBanner(null), 5000);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/users`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        setUsers(data.data.users as TeamUser[]);
      } else {
        flash('error', data.error || 'Failed to load team');
      }
    } catch {
      flash('error', 'Network error loading team');
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeaders]);

  useEffect(() => {
    if (isAdmin) fetchUsers();
    else setLoading(false);
  }, [isAdmin, fetchUsers]);

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-md rounded-xl border border-[var(--border)] bg-[var(--card)] p-6 text-center">
        <ShieldCheck className="mx-auto mb-3 h-8 w-8 text-[var(--muted-foreground)]" aria-hidden />
        <p className="text-sm font-medium text-[var(--foreground)]">Admins only</p>
        <p className="mt-1 text-xs text-[var(--muted-foreground)]">
          Only a portal admin can manage your team's users. Ask your admin to add or update accounts.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      {banner && (
        <div
          className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm ${
            banner.type === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
          role="status"
        >
          {banner.type === 'success' ? (
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" aria-hidden />
          ) : (
            <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden />
          )}
          <span>{banner.text}</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-[var(--primary)]" aria-hidden />
          <div>
            <h2 className="text-sm font-semibold text-[var(--foreground)]">Team members</h2>
            <p className="text-xs text-[var(--muted-foreground)]">
              A maker and a different checker are required to release payment runs.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={fetchUsers}
            className="flex min-h-[36px] items-center gap-1.5 rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-1.5 text-sm text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)]"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="flex min-h-[36px] items-center gap-1.5 rounded-lg bg-[var(--primary)] px-3 py-1.5 text-sm font-medium text-[var(--primary-foreground)] transition-opacity hover:opacity-90"
          >
            <Plus className="h-4 w-4" aria-hidden />
            Add user
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)]">
        {loading ? (
          <div className="p-8 text-center text-sm text-[var(--muted-foreground)]">Loading team…</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--muted-foreground)]">No users yet.</div>
        ) : (
          <ul className="divide-y divide-[var(--border)]">
            {users.map((u) => (
              <li key={u.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-medium text-[var(--foreground)]">{u.name}</p>
                    <span
                      className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize ${ROLE_STYLES[u.role]}`}
                    >
                      {u.role}
                    </span>
                    {!u.isActive && (
                      <span className="inline-flex rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-600">
                        Disabled
                      </span>
                    )}
                    {u.mustChangePassword && u.isActive && (
                      <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                        Must reset password
                      </span>
                    )}
                  </div>
                  <p className="truncate text-xs text-[var(--muted-foreground)]">{u.email}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setEditUser(u)}
                    className="flex min-h-[36px] items-center gap-1.5 rounded-lg border border-[var(--border)] px-2.5 py-1.5 text-xs text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)]"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                    <span className="hidden sm:inline">Edit</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setResetUser(u)}
                    className="flex min-h-[36px] items-center gap-1.5 rounded-lg border border-[var(--border)] px-2.5 py-1.5 text-xs text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)]"
                  >
                    <KeyRound className="h-3.5 w-3.5" aria-hidden />
                    <span className="hidden sm:inline">Reset password</span>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {addOpen && (
        <AddUserModal
          onClose={() => setAddOpen(false)}
          onSaved={(msg) => {
            setAddOpen(false);
            flash('success', msg);
            fetchUsers();
          }}
          onError={(msg) => flash('error', msg)}
          apiBase={apiBase}
          authHeaders={authHeaders}
        />
      )}

      {editUser && (
        <EditUserModal
          user={editUser}
          currentUserId={user?.id != null ? Number(user.id) : null}
          onClose={() => setEditUser(null)}
          onSaved={(msg) => {
            setEditUser(null);
            flash('success', msg);
            fetchUsers();
          }}
          onError={(msg) => flash('error', msg)}
          apiBase={apiBase}
          authHeaders={authHeaders}
        />
      )}

      {resetUser && (
        <ResetPasswordModal
          user={resetUser}
          onClose={() => setResetUser(null)}
          onSaved={(msg) => {
            setResetUser(null);
            flash('success', msg);
            fetchUsers();
          }}
          onError={(msg) => flash('error', msg)}
          apiBase={apiBase}
          authHeaders={authHeaders}
        />
      )}
    </div>
  );
};

// ─── Shared modal shell ───────────────────────────────────────────────────────

const ModalShell: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({
  title,
  onClose,
  children,
}) => (
  <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
    <div className="w-full max-w-md rounded-t-2xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-xl sm:rounded-2xl">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--foreground)]">{title}</h3>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-1 text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)]"
          aria-label="Close"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
      {children}
    </div>
  </div>
);

const fieldClass =
  'w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)] outline-none focus:border-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)]';
const labelClass = 'mb-1 block text-xs font-medium text-[var(--foreground)]';

interface ModalProps {
  apiBase: string;
  onClose: () => void;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
  authHeaders: () => Record<string, string>;
}

const RoleSelect: React.FC<{ value: TeamUser['role']; onChange: (r: TeamUser['role']) => void }> = ({
  value,
  onChange,
}) => (
  <div>
    <label className={labelClass} htmlFor="role-select">Role</label>
    <select
      id="role-select"
      value={value}
      onChange={(e) => onChange(e.target.value as TeamUser['role'])}
      className={fieldClass}
    >
      {ROLE_OPTIONS.map((r) => (
        <option key={r.value} value={r.value}>
          {r.label} — {r.hint}
        </option>
      ))}
    </select>
  </div>
);

const AddUserModal: React.FC<ModalProps> = ({ apiBase, onClose, onSaved, onError, authHeaders }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TeamUser['role']>('checker');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const canSubmit = name.trim() && /.+@.+\..+/.test(email) && passwordValid(password);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}/users`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ name: name.trim(), email: email.trim(), role, password }),
      });
      const data = await res.json();
      if (data.success) onSaved(`${email.trim()} added as ${role}.`);
      else onError(data.error || 'Failed to add user');
    } catch {
      onError('Network error adding user');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Add team member" onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className={labelClass} htmlFor="add-name">Full name</label>
          <input id="add-name" className={fieldClass} value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
        </div>
        <div>
          <label className={labelClass} htmlFor="add-email">Email</label>
          <input id="add-email" type="email" className={fieldClass} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
        </div>
        <RoleSelect value={role} onChange={setRole} />
        <div>
          <label className={labelClass} htmlFor="add-pw">Temporary password</label>
          <input id="add-pw" type="text" className={fieldClass} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" />
          <p className="mt-1 text-[11px] text-[var(--muted-foreground)]">{PASSWORD_HINT} They will be asked to change it at first login.</p>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="rounded-lg border border-[var(--border)] px-3 py-2 text-sm text-[var(--muted-foreground)] hover:bg-[var(--muted)]">Cancel</button>
          <button type="button" disabled={!canSubmit || busy} onClick={submit} className="rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-medium text-[var(--primary-foreground)] disabled:opacity-50">
            {busy ? 'Adding…' : 'Add user'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

const EditUserModal: React.FC<ModalProps & { user: TeamUser; currentUserId: number | null }> = ({
  apiBase,
  user,
  currentUserId,
  onClose,
  onSaved,
  onError,
  authHeaders,
}) => {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [role, setRole] = useState<TeamUser['role']>(user.role);
  const [isActive, setIsActive] = useState(user.isActive);
  const [busy, setBusy] = useState(false);
  const isSelf = currentUserId != null && Number(user.id) === currentUserId;

  const submit = async () => {
    setBusy(true);
    const body: Record<string, unknown> = {};
    if (name.trim() !== user.name) body.name = name.trim();
    if (email.trim().toLowerCase() !== user.email) body.email = email.trim();
    if (role !== user.role) body.role = role;
    if (isActive !== user.isActive) body.isActive = isActive;

    if (Object.keys(body).length === 0) {
      onError('No changes to save');
      setBusy(false);
      return;
    }
    try {
      const res = await fetch(`${apiBase}/users/${user.id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) onSaved(`${email.trim()} updated.`);
      else onError(data.error || 'Failed to update user');
    } catch {
      onError('Network error updating user');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Edit team member" onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className={labelClass} htmlFor="edit-name">Full name</label>
          <input id="edit-name" className={fieldClass} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className={labelClass} htmlFor="edit-email">Email</label>
          <input id="edit-email" type="email" className={fieldClass} value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <RoleSelect value={role} onChange={setRole} />
        <label className="flex items-center gap-2 pt-1 text-sm text-[var(--foreground)]">
          <input
            type="checkbox"
            checked={isActive}
            disabled={isSelf}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-[var(--border)]"
          />
          Account active
          {isSelf && <span className="text-[11px] text-[var(--muted-foreground)]">(you cannot disable yourself)</span>}
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="rounded-lg border border-[var(--border)] px-3 py-2 text-sm text-[var(--muted-foreground)] hover:bg-[var(--muted)]">Cancel</button>
          <button type="button" disabled={busy} onClick={submit} className="rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-medium text-[var(--primary-foreground)] disabled:opacity-50">
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

const ResetPasswordModal: React.FC<ModalProps & { user: TeamUser }> = ({
  apiBase,
  user,
  onClose,
  onSaved,
  onError,
  authHeaders,
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const canSubmit = passwordValid(newPassword);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}/users/${user.id}/reset-password`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ newPassword }),
      });
      const data = await res.json();
      if (data.success) onSaved(`Password reset for ${user.email}.`);
      else onError(data.error || 'Failed to reset password');
    } catch {
      onError('Network error resetting password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title={`Reset password — ${user.name}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-[var(--muted-foreground)]">
          Set a new temporary password for <span className="font-medium text-[var(--foreground)]">{user.email}</span>. They will be asked to change it at next login.
        </p>
        <div>
          <label className={labelClass} htmlFor="reset-pw">New password</label>
          <input id="reset-pw" type="text" className={fieldClass} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="off" />
          <p className="mt-1 text-[11px] text-[var(--muted-foreground)]">{PASSWORD_HINT}</p>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="rounded-lg border border-[var(--border)] px-3 py-2 text-sm text-[var(--muted-foreground)] hover:bg-[var(--muted)]">Cancel</button>
          <button type="button" disabled={!canSubmit || busy} onClick={submit} className="rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-medium text-[var(--primary-foreground)] disabled:opacity-50">
            {busy ? 'Resetting…' : 'Reset password'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
};
